import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { FaBars, FaTimes } from "react-icons/fa";
import logo from "../assets/img/logo2.png";

export default function Header({ shrink }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when screen size changes
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Scroll to features section
  const scrollToFeatures = () => {
    const el = document.getElementById("features");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Handler for Features click
  const handleFeaturesClick = (e) => {
    e.preventDefault();
    setIsMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: "features" } });
    } else {
      scrollToFeatures();
    }
  };

  return (
    <header
      className={`w-full sticky top-0 z-50 transition-all duration-500 ${
        scrolled || shrink
          ? "bg-white/90 backdrop-blur-xl py-1 sm:py-2 shadow-lg border-b border-orange-200"
          : "bg-gradient-to-r from-black via-gray-900 to-gray-800 py-2 sm:py-3 shadow-xl"
      }`}
      style={{
        backdropFilter: "blur(12px)",
        minHeight: 56,
      }}
    >
      <div
        className={`max-w-7xl mx-auto flex items-center justify-between px-3 sm:px-4 md:px-6 lg:px-8 transition-all duration-300 ${
          scrolled || shrink
            ? "gap-2 sm:gap-3 md:gap-4"
            : "gap-3 sm:gap-4 md:gap-6"
        }`}
      >
        {/* Logo Section */}
        <div className="flex items-center h-full">
          <img
            src={logo}
            alt="EasiDine Logo"
            className="h-12 w-auto sm:h-16 md:h-20 lg:h-[72px] object-contain -my-1 sm:-my-2"
            style={{ maxHeight: "none" }}
          />
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex gap-6 xl:gap-8">
          {[
            { name: "Home", to: "/" },
            { name: "Features", to: "#features" },
            { name: "About", to: "/about" },
            { name: "Contact", to: "/contact" },
          ].map((item) =>
            item.name === "Features" ? (
              <button
                key={item.name}
                onClick={handleFeaturesClick}
                className={`relative font-medium transition-all duration-300 group bg-transparent outline-none ${
                  scrolled || shrink
                    ? "text-black hover:text-orange-600 text-base xl:text-lg"
                    : "text-gray-100 hover:text-orange-400 text-base xl:text-lg"
                }`}
                style={{ cursor: "pointer" }}
              >
                {item.name}
                <span
                  className={`absolute -bottom-1 left-0 w-0 h-0.5 ${
                    scrolled || shrink
                      ? "bg-gradient-to-r from-orange-500 to-red-500"
                      : "bg-gradient-to-r from-orange-400 to-yellow-400"
                  } transition-all duration-300 group-hover:w-full`}
                ></span>
              </button>
            ) : (
              <Link
                key={item.name}
                to={item.to}
                className={`relative font-medium transition-all duration-300 group ${
                  scrolled || shrink
                    ? "text-black hover:text-orange-600 text-base xl:text-lg"
                    : "text-gray-100 hover:text-orange-400 text-base xl:text-lg"
                }`}
              >
                {item.name}
                <span
                  className={`absolute -bottom-1 left-0 w-0 h-0.5 ${
                    scrolled || shrink
                      ? "bg-gradient-to-r from-orange-500 to-red-500"
                      : "bg-gradient-to-r from-orange-400 to-yellow-400"
                  } transition-all duration-300 group-hover:w-full`}
                ></span>
              </Link>
            )
          )}
        </nav>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 sm:gap-3">
          <Link
            to="/login"
            className="px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-white bg-gradient-to-r from-orange-500 to-red-500 rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
          >
            Login
          </Link>
          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-1.5 sm:p-2 text-gray-700 hover:text-orange-600 transition-colors duration-300"
            style={{ minWidth: 44, minHeight: 44 }}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <FaTimes className="text-lg sm:text-xl" />
            ) : (
              <FaBars className="text-lg sm:text-xl" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-orange-200 animate-slideDown">
          <nav className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 space-y-1 sm:space-y-2 md:space-y-3">
            {[
              { name: "Home", to: "/" },
              { name: "Features", to: "#features" },
              { name: "About", to: "/about" },
              { name: "Contact", to: "/contact" },
            ].map((item) =>
              item.name === "Features" ? (
                <button
                  key={item.name}
                  onClick={handleFeaturesClick}
                  className="block text-black hover:text-orange-600 font-medium py-2.5 sm:py-3 text-base sm:text-lg md:text-xl transition-colors duration-300 rounded-lg px-2 bg-transparent outline-none w-full text-left"
                  style={{ minWidth: 44, minHeight: 44, cursor: "pointer" }}
                >
                  {item.name}
                </button>
              ) : item.to.startsWith("#") ? (
                <a
                  key={item.name}
                  href={item.to}
                  className="block text-black hover:text-orange-600 font-medium py-2.5 sm:py-3 text-base sm:text-lg md:text-xl transition-colors duration-300 rounded-lg px-2"
                  style={{ minWidth: 44, minHeight: 44 }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </a>
              ) : (
                <Link
                  key={item.name}
                  to={item.to}
                  className="block text-black hover:text-orange-600 font-medium py-2.5 sm:py-3 text-base sm:text-lg md:text-xl transition-colors duration-300 rounded-lg px-2"
                  style={{ minWidth: 44, minHeight: 44 }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              )
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
